// <featured-collection-slider> — the product slider of sections/featured-collection.liquid.
// A Splide (assets/splide.min.js) wrapper like <splide-component> (assets/multiverse.js), with the
// extra per-breakpoint options that section exposes. The slider is set up from data attributes:
//   data-type                slide | loop
//   data-autoplay            "true" | "false";  data-autoplay-speed  seconds between moves
//   data-slides-desktop      slides per page on desktop (= columns);  data-per-move-desktop
//   data-gap-desktop         px between slides;  data-padding-desktop  px preview of prev/next slide
//   data-padding-calc-desktop "true" keeps the first slide on the left edge (padding only on the
//                            right; the stylesheet widens the track by the same amount)
//   data-adaptive-desktop    "true" → autoHeight (the section only sets it for 1 slide per page)
//   data-destroy-desktop     "true" → no slider on desktop (the list falls back to the CSS grid)
//   data-slides-mobile / data-gap-mobile / data-padding-mobile / data-adaptive-mobile /
//   data-destroy-mobile      the same for viewports up to 749px
// Arrows and dots use the placeholders in the markup (.splide__arrows with its two buttons and
// .splide__pagination); their visibility per breakpoint is handled by the stylesheet, so Splide
// always wires them.
(() => {
  const MOBILE_BREAKPOINT = 749;
  const define = (name, cls) => {
    if (window.Multiverse && typeof window.Multiverse.defineOnce === 'function') {
      window.Multiverse.defineOnce(name, cls);
    } else if (!window.customElements.get(name)) {
      window.customElements.define(name, cls);
    }
  };
  const int = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };
  const flag = (value) => value === 'true';

  class FeaturedCollectionSlider extends HTMLElement {
    connectedCallback() {
      this._onSectionLoad = this._onSectionLoad || ((event) => {
        if (event.target === this || (event.target && event.target.contains && event.target.contains(this))) {
          this.mount();
        }
      });
      document.addEventListener('shopify:section:load', this._onSectionLoad);
      this.mount();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      this.destroy();
    }

    mount() {
      this.destroy();
      const root = this.querySelector('.splide');
      if (!root) return;

      if (typeof window.Splide === 'undefined') {
        // splide.min.js is deferred from the layout ahead of this file; guard the odd load order.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.mount(), { once: true });
        } else if (!this._retryTimer) {
          this._retryTimer = window.setTimeout(() => {
            this._retryTimer = null;
            this.mount();
          }, 50);
        }
        return;
      }

      const d = this.dataset;
      const desktopPadding = Math.max(0, int(d.paddingDesktop, 0));
      const mobilePadding = Math.max(0, int(d.paddingMobile, 15));
      const perPageDesktop = Math.max(1, int(d.slidesDesktop, 3));
      const perPageMobile = Math.max(1, int(d.slidesMobile, 1));
      const perMoveDesktop = Math.min(perPageDesktop, Math.max(1, int(d.perMoveDesktop, 1)));

      const options = {
        type: d.type === 'loop' ? 'loop' : 'slide',
        autoplay: flag(d.autoplay),
        interval: Math.max(0.5, parseFloat(d.autoplaySpeed) || 5) * 1000,
        pauseOnHover: true,
        pauseOnFocus: true,
        arrows: true,
        pagination: true,
        speed: 500,
        // Reference slider behaviour: every slide is a page (focus 0) and pages that would only
        // repeat the last slides are dropped (omitEnd), so 4 slides at 2 per page give 3 dots.
        focus: 0,
        omitEnd: true,
        trimSpace: true,
        perPage: perPageDesktop,
        perMove: perMoveDesktop,
        gap: `${Math.max(0, int(d.gapDesktop, 40))}px`,
        padding: flag(d.paddingCalcDesktop) ? { left: 0, right: desktopPadding } : desktopPadding,
        autoHeight: flag(d.adaptiveDesktop),
        destroy: flag(d.destroyDesktop),
        breakpoints: {
          [MOBILE_BREAKPOINT]: {
            perPage: perPageMobile,
            // The schema has no mobile per-move setting: one slide per move (the reference default).
            perMove: 1,
            gap: `${Math.max(0, int(d.gapMobile, 15))}px`,
            padding: mobilePadding,
            autoHeight: flag(d.adaptiveMobile),
            destroy: flag(d.destroyMobile),
          },
        },
      };

      this.splide = new window.Splide(root, options);
      this.splide.mount();
    }

    destroy() {
      if (this.splide) {
        this.splide.destroy();
        this.splide = null;
      }
      if (this._retryTimer) {
        window.clearTimeout(this._retryTimer);
        this._retryTimer = null;
      }
    }
  }

  define('featured-collection-slider', FeaturedCollectionSlider);
})();
